import React, {useState, useEffect, useMemo} from 'react';
import useApiControl from '../../../common/useApiControl';
import RoomUserSP from '../RoomUserSP';
import AddFollowRivalDialog from '../../../components/dialogs/AddFollowRivalDialog'
import {useSSEData} from '../../../common/useSseControl';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, Typography, IconButton, Button } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

function RoomUsersSP(props) 
{
  const event_id = props.event_id;
  const room_id = props.room_id;
  const onReady = props.onReady;

  const [exceptionOccurred, setExceptionOccurred] = useState(false);
  const [addFollowRivalDialogVisible, setAddFollowRivalDialogVisible] = useState(false);
  const [dialogDatas, setDialogDatas] = useState({
    targetUser: { user_id: null }, 
    user: { id: null },
  });
  const { sseData, sseError } = useSSEData();
  
  const [userDatas, setUserDatas] = useState(null);
  const pushUserDatas = function(pushDatas) {
    if (userDatas) {
      const existingIds = new Set(userDatas.all.map(u => u.user_id));
      const newUsers = pushDatas.filter(u => !existingIds.has(u.user_id));
      const newFollow = newUsers.filter(u => follow.includes(u.user_id));
      const newRivals = newUsers.filter(u => rivals.includes(u.user_id));
  
      if (newUsers.length > 0) {
        setUserDatas({
          ...userDatas,
          follow: [...userDatas.follow, ...newFollow],
          rivals: [...userDatas.rivals, ...newRivals],
          all: [...userDatas.all, ...newUsers]
        });
      }
    }
  }
  
  const [follow, setFollow] = useState(JSON.parse(props.user.follow));
  const isFollowed = function(id) {
      return follow.includes(id);
  }
  const addFollow = function(id) {
    setFollow([...follow, id]);

    if (userDatas) {
      const newFollowList = [...userDatas.follow];
      const moved = userDatas.all.find(u => u.user_id === id);
      if (moved) {
        newFollowList.push(moved);
        setUserDatas({
          ...userDatas,
          follow: newFollowList,
        });
      }
    }
  }
  const removeFollow = function(id) {
    setFollow(follow.filter(item => item !== id));
  
    if (userDatas) {
      const removed = userDatas.follow.find(u => u.user_id === id);
      if (removed) {
        const newFollowList = userDatas.follow.filter(u => u.user_id !== id);
        setUserDatas({
          ...userDatas,
          follow: newFollowList,
        });
      }
    }
  };
  
  const [rivals, setRivals] = useState(JSON.parse(props.user.rivals));
  const isRivals = function(id) {
      return rivals.includes(id);
  }
  const addRivals = function(id) {
    setRivals([...rivals, id]);
  
    if (userDatas) {
      const newRivalsList = [...userDatas.rivals];
      const moved = userDatas.all.find(u => u.user_id === id);
      if (moved) {
        newRivalsList.push(moved);
        setUserDatas({
          ...userDatas,
          rivals: newRivalsList,
        });
      }
    }
  }    
  const removeRivals = function(id) {
    setRivals(rivals.filter(item => item !== id));

    if (userDatas) {
      const removed = userDatas.rivals.find(u => u.user_id === id);
      if (removed) {
        const newRivalsList = userDatas.rivals.filter(u => u.user_id !== id);
        setUserDatas({
          ...userDatas,
          rivals: newRivalsList,
        });
      }
    }
  }
  
  const params = useMemo(() => ({
    event_id: event_id,
    max: 20,
  }), [event_id]);
  const { data, apiError, loading, executeRequest } = useApiControl({
      action: '/get_event_users',
      params: params,
      method: 'POST',
  });

  useEffect(() => {
    if (data) {
      const calcWinRate = (user) => {
        const wins = user.wins ?? 0;
        const rounds = user.rounds_played ?? 0;

        if (rounds === 0) return 0;

        const rate = (wins / rounds) * 100;
        return Math.round(rate * 10) / 10;
      };

      const addWinRate = (users) =>
        users.map(user => ({
          ...user,
          winRate: calcWinRate(user),
        }));

      setUserDatas({
        all: addWinRate(data.all),
        follow: addWinRate(data.follow),
        rivals: addWinRate(data.rivals),
      });
    }
  }, [data]);

  useEffect(() => {
    if (userDatas) {
      onReady?.();
    }
  }, [userDatas, onReady]);

  useEffect(() => {
      if (exceptionOccurred) return;

      executeRequest().catch((error) => {
          console.error(error);
          setExceptionOccurred(true);
      });
    }, [executeRequest, exceptionOccurred]);

    const roomUserClick = function(targetUser) {
      setDialogDatas({
        targetUser:targetUser,
        user:props.user
      });
      if(props.user.id === targetUser?.user_id) {
          setAddFollowRivalDialogVisible(false);
        } else {
          setAddFollowRivalDialogVisible(true);
        }
    }

  useEffect(() => {
    if (
      sseData?.type === 'direct' &&
      sseData.action === 'push' &&
      sseData.target === 'userList' &&
      Array.isArray(sseData.payload)
    ) {
      pushUserDatas(sseData.payload);
    }
  }, [sseData]);

  // --- Paging States ---
  const [activeTab, setActiveTab] = useState('follow'); // 'follow', 'rivals', 'all'
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 5;

  const tabLabels = {
    follow: 'フォロー中のユーザー',
    rivals: 'ライバル',
    all: '全ユーザー',
  };
  const tabOrder = ['follow', 'rivals', 'all'];

  // --- Handlers ---
  const handleTabChange = (direction) => {
    const currentIndex = tabOrder.indexOf(activeTab);
    let nextIndex;
    if (direction === 'next') {
      nextIndex = (currentIndex + 1) % tabOrder.length;
    } else {
      nextIndex = (currentIndex - 1 + tabOrder.length) % tabOrder.length;
    }
    setActiveTab(tabOrder[nextIndex]);
    setCurrentPage(1); // Reset page on tab change
  };

  const handlePageChange = (direction) => {
    if (direction === 'next') {
      setCurrentPage((prev) => prev + 1);
    } else {
      setCurrentPage((prev) => prev - 1);
    }
  };

  if (exceptionOccurred) {
      alert('エラーが発生しました。再ログインしてくださいです。。。')
  }

  const headerCellStyle = {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#444',
    padding: '6px 8px',
    textAlign: 'center',
    lineHeight: '1.4',
    borderBottom: '2px solid #ccc',
    backgroundColor: '#f5f5f5',
  };
  
  if (userDatas) {
    const currentUsers = userDatas[activeTab] || [];
    const totalPages = Math.ceil(currentUsers.length / PAGE_SIZE);
    const paginatedUsers = currentUsers.slice(
      (currentPage - 1) * PAGE_SIZE,
      currentPage * PAGE_SIZE
    );

    return (
      <>
        <TableContainer component={Paper}
          style={{
            margin: '24px auto',
            borderRadius: '8px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            maxWidth: '600px',
            width: '100%',
            overflowX: 'auto',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, borderBottom: '1px solid #e0e0e0', backgroundColor: '#e0e0e0' }}>
            <IconButton onClick={() => handleTabChange('prev')} size="small">
              <ArrowBackIosNewIcon fontSize="inherit" />
            </IconButton>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '12px', color: '#333' }}>
              {tabLabels[activeTab]}
            </Typography>
            <IconButton onClick={() => handleTabChange('next')} size="small">
              <ArrowForwardIosIcon fontSize="inherit" />
            </IconButton>
          </Box>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ ...headerCellStyle, width: '40px' }}>順位</TableCell>
                <TableCell style={{ ...headerCellStyle, minWidth: '80px' }}>名前</TableCell>
                <TableCell style={{ ...headerCellStyle, width: '50px' }}>収支</TableCell>
                <TableCell style={{ ...headerCellStyle, width: '50px' }}>勝率</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.length > 0 ? (
                paginatedUsers.map((tableUser, index) => (
                  <RoomUserSP
                    user={tableUser}
                    index={(currentPage - 1) * PAGE_SIZE + index}
                    key={tableUser.user_id}
                    isFollow={isFollowed(tableUser.user_id)}
                    isRivals={isRivals(tableUser.user_id)}
                    onClick={() => roomUserClick(tableUser)}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" style={{ padding: '16px' }}>
                    ユーザーがいません
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, borderTop: '1px solid #e0e0e0' }}>
              <Button onClick={() => handlePageChange('prev')} disabled={currentPage === 1}>
                &lt;&lt;
              </Button>
              <Typography sx={{ mx: 2 }}>
                {currentPage} / {totalPages}
              </Typography>
              <Button onClick={() => handlePageChange('next')} disabled={currentPage === totalPages}>
                &gt;&gt;
              </Button>
            </Box>
          )}
        </TableContainer>      
        <AddFollowRivalDialog
          isVisible={addFollowRivalDialogVisible}
          onClose={() => setAddFollowRivalDialogVisible(false)}
          dialogDatas={dialogDatas}
          isFollowed={(id)=> isFollowed(id)}
          addFollow={(id)=> addFollow(id)}
          removeFollow={(id)=> removeFollow(id)}
          isRivals={(id)=> isRivals(id)}
          addRivals={(id)=> addRivals(id)}
          removeRivals={(id)=> removeRivals(id)}
        />  
      
      
      </>


    )
  }
}

export default RoomUsersSP;
